import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { CountrySelect } from '@/components/ui/country-select'
import { useDetectedCountry } from '@/hooks/useDetectedCountry'
import { useCountries } from '@/hooks/useCountries'
import { formatCents } from '@/lib/utils'
import {
  CheckIcon,
  XIcon,
  ArrowRightIcon,
  ZapIcon,
  ShieldIcon,
  UsersIcon,
  RefreshCwIcon,
  LockIcon,
  FileTextIcon,
  CreditCardIcon,
  GlobeIcon,
  HeadphonesIcon,
  BadgeCheckIcon,
  DatabaseIcon,
  ChevronRightIcon,
  ArrowUpRightIcon,
  RocketIcon,
  TrendingDownIcon,
  ClockIcon,
  MinusIcon,
  PlusIcon,
  BanknoteIcon,
} from 'lucide-react'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const sectionPy = 'py-20 sm:py-28 lg:py-36'
const containerW = 'mx-auto max-w-7xl px-6 sm:px-8 lg:px-12'

// EUR base prices per tier (cents) — used only as the fallback before country
// detection resolves or when the detected country has no active plan. Real
// prices come from GET /api/v1/countries/detect via useDetectedCountry().
const GROWTH_MONTHLY_CENTS = 700
const GROWTH_ANNUAL_CENTS = 500 // per month, billed annually (~29% off)
const ENTERPRISE_MONTHLY_CENTS = 1500
const ENTERPRISE_ANNUAL_CENTS = 1200 // per month, billed annually (20% off)

const BANDS = [
  { id: 'A', label: 'Premium', multiplier: 1.0, examples: 'US, UK, IE, AU, CH, SG, AE' },
  { id: 'B', label: 'Developed', multiplier: 0.75, examples: 'MX, BR, PL, CZ, TR, ZA' },
  { id: 'C', label: 'Emerging', multiplier: 0.5, examples: 'NG, GH, KE, IN, PH, VN' },
  { id: 'D', label: 'Frontier', multiplier: 0.3, examples: 'All other countries' },
]

// ─────────────────────────────────────────────────────────────
// Feature rows for the comparison table
// ─────────────────────────────────────────────────────────────
type FeatureRow = { feature: string; growth: string | boolean; enterprise: string | boolean; icon: React.ElementType }

const features: FeatureRow[] = [
  { feature: 'Funding circles', growth: 'Unlimited', enterprise: 'Unlimited', icon: RefreshCwIcon },
  { feature: 'Payment cycle tracking', growth: true, enterprise: true, icon: CreditCardIcon },
  { feature: 'Mobile app access', growth: true, enterprise: true, icon: UsersIcon },
  { feature: 'CSV bulk user import', growth: true, enterprise: true, icon: DatabaseIcon },
  { feature: 'Tax form generation', growth: '1 country', enterprise: 'Multi-country', icon: FileTextIcon },
  { feature: 'SSO (SAML 2.0 / OIDC)', growth: false, enterprise: true, icon: LockIcon },
  { feature: 'JIT user provisioning', growth: false, enterprise: true, icon: ShieldIcon },
  { feature: 'Priority support (SLA)', growth: false, enterprise: true, icon: HeadphonesIcon },
  { feature: 'Dedicated account manager', growth: false, enterprise: true, icon: BadgeCheckIcon },
  { feature: 'SOC 2 compliance reports', growth: false, enterprise: true, icon: ShieldIcon },
  { feature: 'Multi-country operations', growth: true, enterprise: true, icon: GlobeIcon },
]

// ─────────────────────────────────────────────────────────────
// Add-on rows
// ─────────────────────────────────────────────────────────────
const addons = [
  { name: 'Extra country tax filing', price: '€200/country/year' },
  { name: 'API access', price: '€100/month base' },
  { name: 'Bulk import beyond 10K rows', price: 'Tiered by volume' },
]

// ─────────────────────────────────────────────────────────────
// FAQ items
// ─────────────────────────────────────────────────────────────
const faqs = [
  {
    q: 'Is there a free trial?',
    a: 'Yes — every new organisation gets a 90-day free trial on the Growth plan with full access to all features. No credit card required to start.',
  },
  {
    q: 'What is the minimum monthly charge?',
    a: 'Every organisation pays a minimum of €30/month (or local equivalent), even if your per-user calculation is lower. This ensures the platform remains sustainable for small teams.',
  },
  {
    q: 'How does regional pricing work?',
    a: 'Pricing adjusts based on your country\'s purchasing power — employers in emerging markets pay up to 50% less than the base rate. The prices on this page are already adjusted for the country we detected. Your exact price is also confirmed during signup — no surprises.',
  },
  {
    q: 'Can I switch between monthly and annual billing?',
    a: 'Yes. You can switch to annual billing at any time to lock in the discounted rate. Switching from annual to monthly takes effect at the end of your current billing cycle.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'You\'ll receive reminders before your trial expires. If you don\'t choose a plan, your organisation enters a grace period — existing payment cycles continue uninterrupted, but new activity is paused.',
  },
  {
    q: 'Do I need Enterprise for SSO?',
    a: 'Yes — SSO (SAML 2.0 and OIDC) is available exclusively on the Enterprise plan. We support Okta, Azure AD, and any SAML/OIDC-compliant identity provider.',
  },
  {
    q: 'Is my data secure?',
    a: 'PayrollPeer is SOC 2 Type II ready. We use AES-256 encryption at rest, TLS 1.3 in transit, and operate on a non-custodial model — we never hold employee funds.',
  },
]

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function CellValue({ value }: { value: string | boolean }) {
  if (typeof value === 'string') {
    return <span className="text-sm font-medium text-white">{value}</span>
  }
  return value ? (
    <CheckIcon className="mx-auto size-5 text-[#00E5A0]" />
  ) : (
    <XIcon className="mx-auto size-5 text-white/20" />
  )
}

// ─────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────
function PricingPage() {
  const [annual, setAnnual] = useState(false)
  const { country, plan, enterprise_plan: enterprisePlan, loading: countryLoading, setOverride } = useDetectedCountry()
  const { countries, loading: countriesLoading } = useCountries()

  // The dropdown is driven by `country` — what the rendered prices actually
  // reflect — so the select, the "Prices shown for" label, and the numbers
  // never disagree, even while an override fetch is in flight or failed.
  const selectedCountryId = countries.find((c) => c.iso_2 === country?.iso_2)?.country_id ?? ''

  const handleCountryOverride = (value: number | '') => {
    if (value === '') {
      setOverride(null)
      return
    }
    const picked = countries.find((c) => c.country_id === value)
    if (picked?.iso_2) setOverride(picked.iso_2)
  }

  // Monthly prices (cents) come from the detected country's active plans. Annual
  // prices are derived with the same discounts the signup page quotes — Growth
  // 5/7 (≈29% off), Enterprise 0.8 (20% off) — which reproduce the seeded
  // annual plan prices exactly for every regional band multiplier.
  const growthMonthlyCents = plan?.price_per_user_cents ?? GROWTH_MONTHLY_CENTS
  const growthAnnualCents = Math.round(growthMonthlyCents * (GROWTH_ANNUAL_CENTS / GROWTH_MONTHLY_CENTS))
  const enterpriseMonthlyCents = enterprisePlan?.price_per_user_cents ?? ENTERPRISE_MONTHLY_CENTS
  const enterpriseAnnualCents = Math.round(enterpriseMonthlyCents * (ENTERPRISE_ANNUAL_CENTS / ENTERPRISE_MONTHLY_CENTS))

  const growthCents = annual ? growthAnnualCents : growthMonthlyCents
  const enterpriseCents = annual ? enterpriseAnnualCents : enterpriseMonthlyCents
  // Numeric value (EUR-denominated) for the ROI calculator math; display uses
  // formatCents() so cents never round-trip through floats.
  const growthPrice = growthCents / 100

  const currencySymbol = plan?.currency_symbol ?? '€'
  const minimumMonthly = formatCents(plan?.minimum_monthly_cents ?? 3000)

  // ── ROI Calculator state ──
  const [roiEmployees, setRoiEmployees] = useState(25)
  const [roiCyclesPerMonth, setRoiCyclesPerMonth] = useState(2)
  const [roiHourlyRate, setRoiHourlyRate] = useState(35)
  const [roiMinutesPerFile, setRoiMinutesPerFile] = useState(45)
  const [roiCountries, setRoiCountries] = useState(1)

  // Derived ROI values — Growth plan comparison (band A = 1.0×, €7/user/month)
  const roiManualMonthlyCost =
    roiCyclesPerMonth * roiCountries * (roiMinutesPerFile / 60) * roiHourlyRate
  const roiPlatformMonthly = Math.max(growthPrice * roiEmployees, 30)
  const roiMonthlySavings = roiManualMonthlyCost - roiPlatformMonthly
  const roiAnnualSavings = roiMonthlySavings * 12
  const roiHoursSavedPerYear = roiCyclesPerMonth * roiCountries * (roiMinutesPerFile / 60) * 12

  return (
    <div className="min-h-svh bg-[#0B1120]">
      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16 lg:pt-40 lg:pb-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 size-[600px] rounded-full bg-[#00E5A0]/8 blur-[140px]" />

        <div className={`${containerW} relative z-10 text-center`}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00E5A0]/30 bg-[#00E5A0]/10 px-4 py-1.5 text-xs font-medium text-[#00E5A0]">
            <ZapIcon className="size-3.5" />
            Simple, transparent pricing
          </div>

          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] tracking-tight text-white">
            One platform. <span className="text-[#00E5A0]">Two plans.</span>
            <br />
            Priced per employee.
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base sm:text-lg text-white/50 leading-relaxed">
            Every employer gets a 90-day free trial. Regional pricing adjusts
            automatically based on your country. No hidden fees.
          </p>

          {/* Monthly / Annual toggle */}
          <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                !annual
                  ? 'bg-[#00E5A0] text-[#0B1120] shadow-lg shadow-[#00E5A0]/20'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                annual
                  ? 'bg-[#00E5A0] text-[#0B1120] shadow-lg shadow-[#00E5A0]/20'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Annual
              <span className="ml-1.5 rounded-full bg-[#00E5A0]/20 px-2 py-0.5 text-[10px] font-bold text-[#00E5A0]">
                Save 29%
              </span>
            </button>
          </div>

          {/* Country indicator + manual override dropdown (DESIGN §9.3) — shows
              the jurisdiction the prices below are based on and lets visitors
              correct a wrong detection. Picking a country refetches its plans
              via ?iso_2= and persists the choice for /signup. */}
          {country ? (
            <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/50">
              <label className="sr-only" htmlFor="country-override">
                Pricing country
              </label>
              <span>Prices shown for</span>
              <CountrySelect
                id="country-override"
                value={selectedCountryId}
                onChange={handleCountryOverride}
                countries={countries}
                includeEmptyOption
                emptyLabel="Auto-detect my country"
                disabled={countriesLoading}
                className="min-w-[150px]"
              />
              {country.pricing_band && (
                <span className="rounded-full bg-[#00E5A0]/10 px-2 py-0.5 text-[10px] font-bold text-[#00E5A0]">
                  Band {country.pricing_band}
                </span>
              )}
            </div>
          ) : countryLoading ? (
            <div className="mt-4 animate-pulse text-xs text-white/30">Detecting your country…</div>
          ) : null}
        </div>
      </section>

      {/* ─── PRICING CARDS ─── */}
      <section className="pb-20 sm:pb-28">
        <div className={`${containerW}`}>
          <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2">
            {/* ── Growth ── */}
            <div className="relative flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-[#00E5A0]/15 text-[#00E5A0]">
                  <ZapIcon className="size-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Growth</h2>
                  <p className="text-xs text-white/40">Self-serve — start instantly</p>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-5xl font-bold text-white ${
                      countryLoading && !plan ? 'animate-pulse' : ''
                    }`}
                  >
                    {currencySymbol}{formatCents(growthCents)}
                  </span>
                  <span className="text-sm text-white/40">/user/month</span>
                </div>
                {annual && (
                  <p className="mt-1 text-xs text-[#00E5A0]">
                    Billed annually at {currencySymbol}
                    {formatCents(growthAnnualCents * 12)}/user/year
                  </p>
                )}
                <p className="mt-3 text-sm text-white/40">
                  {currencySymbol}{minimumMonthly}/month minimum &middot; 90-day free trial included
                </p>
              </div>

              <a href="/signup">
                <Button
                  size="lg"
                  className="mt-8 w-full bg-white/10 text-white hover:bg-white/15 font-semibold text-base py-6"
                >
                  Start free trial
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </a>

              <div className="mt-8 border-t border-white/10 pt-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/30">
                  Everything you need
                </p>
                <ul className="space-y-3">
                  {[
                    'Unlimited funding circles',
                    'Payment cycle tracking',
                    'Mobile app access',
                    'CSV bulk user import',
                    'Tax form generation (1 country)',
                    'Multi-country operations',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                      <CheckIcon className="mt-0.5 size-4 shrink-0 text-[#00E5A0]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ── Enterprise ── */}
            <div className="relative flex flex-col rounded-3xl border-2 border-[#00E5A0]/30 bg-[#00E5A0]/[0.04] p-8 sm:p-10">
              {/* Recommended badge */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#00E5A0] px-4 py-1 text-xs font-bold text-[#0B1120] shadow-lg shadow-[#00E5A0]/30">
                Most popular
              </div>

              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-[#00E5A0]/20 text-[#00E5A0]">
                  <ShieldIcon className="size-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Enterprise</h2>
                  <p className="text-xs text-white/40">Sales-led — custom contract</p>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-5xl font-bold text-white ${
                      countryLoading && !enterprisePlan ? 'animate-pulse' : ''
                    }`}
                  >
                    {currencySymbol}{formatCents(enterpriseCents)}
                  </span>
                  <span className="text-sm text-white/40">/user/month</span>
                </div>
                {annual && (
                  <p className="mt-1 text-xs text-[#00E5A0]">
                    Billed annually at {currencySymbol}
                    {formatCents(enterpriseAnnualCents * 12)}/user/year
                  </p>
                )}
                <p className="mt-3 text-sm text-white/40">
                  Custom contracts available &middot; Volume discounts for 500+ users
                </p>
              </div>

              <a href="#book-demo">
                <Button
                  size="lg"
                  className="mt-8 w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/20"
                >
                  Book a demo
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </a>

              <div className="mt-8 border-t border-[#00E5A0]/20 pt-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-[#00E5A0]/60">
                  Everything in Growth, plus
                </p>
                <ul className="space-y-3">
                  {[
                    'SSO (SAML 2.0 / OIDC)',
                    'JIT user provisioning',
                    'Multi-country tax filing',
                    'Priority support (SLA-backed)',
                    'Dedicated account manager',
                    'SOC 2 compliance reports',
                    'Custom onboarding',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                      <CheckIcon className="mt-0.5 size-4 shrink-0 text-[#00E5A0]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── REGIONAL PRICING BANDS ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Fair pricing worldwide
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Regional pricing bands
            </h2>
            <p className="mt-4 text-base text-white/50 leading-relaxed">
              Your price adjusts automatically based on your country's purchasing
              power. The base rate is in EUR — you pay in your local currency.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03]">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-white/40">
                      Band
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white/40">
                      Growth
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white/40">
                      Enterprise
                    </th>
                    <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-white/40">
                      Example countries
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {BANDS.map((band, i) => {
                    // The bands table shows band-adjusted prices using the same
                    // currency as the pricing cards above. The visitor's own band
                    // is highlighted.
                    const bandGrowthBase = annual ? GROWTH_ANNUAL_CENTS : GROWTH_MONTHLY_CENTS
                    const bandEnterpriseBase = annual ? ENTERPRISE_ANNUAL_CENTS : ENTERPRISE_MONTHLY_CENTS
                    const gPrice = formatCents(bandGrowthBase * band.multiplier)
                    const ePrice = formatCents(bandEnterpriseBase * band.multiplier)
                    const isYourBand = country?.pricing_band === band.id
                    return (
                      <tr
                        key={band.id}
                        className={`border-b border-white/5 ${
                          isYourBand
                            ? 'bg-[#00E5A0]/[0.06]'
                            : i % 2 === 0
                              ? ''
                              : 'bg-white/[0.02]'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span
                              className={`flex size-8 items-center justify-center rounded-lg text-xs font-bold ${
                                isYourBand
                                  ? 'bg-[#00E5A0] text-[#0B1120]'
                                  : 'bg-[#00E5A0]/10 text-[#00E5A0]'
                              }`}
                            >
                              {band.id}
                            </span>
                            <div>
                              <span className="text-sm font-medium text-white">{band.label}</span>
                              <span className="ml-2 text-xs text-white/30">{band.multiplier}×</span>
                              {isYourBand && (
                                <span className="ml-2 rounded-full bg-[#00E5A0]/15 px-2 py-0.5 text-[10px] font-bold text-[#00E5A0]">
                                  Your band
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-lg font-semibold text-white">{currencySymbol}{gPrice}</span>
                          <span className="text-xs text-white/30">/user</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-lg font-semibold text-white">{currencySymbol}{ePrice}</span>
                          <span className="text-xs text-white/30">/user</span>
                        </td>
                        <td className="hidden sm:table-cell px-6 py-4 text-sm text-white/40">
                          {band.examples}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-center text-xs text-white/30">
              Prices shown are per user per month. €30 monthly minimum applies across all bands.
              Stripe handles currency conversion at live rates.
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHICH PLAN? DECISION GUIDE ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Which plan?
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Find the right fit
            </h2>
            <p className="mt-4 text-base text-white/50 leading-relaxed">
              Most teams start on Growth and upgrade when they need enterprise-grade
              controls. Here's how to decide.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-8 lg:grid-cols-2">
            {/* Growth scenarios */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 sm:p-10">
              <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-[#00E5A0]/10">
                <ZapIcon className="size-6 text-[#00E5A0]" />
              </div>
              <h3 className="text-lg font-bold text-white">Choose Growth if…</h3>
              <ul className="mt-5 space-y-4">
                {[
                  { text: 'You have fewer than 100 employees', detail: 'Self-serve setup, affordable per-user pricing' },
                  { text: 'You operate in a single country', detail: 'One set of payment files, one tax jurisdiction' },
                  { text: 'Email/password login works for your team', detail: 'No SSO requirement from IT or security' },
                  { text: 'You want to start immediately', detail: '90-day free trial, no credit card, no sales call' },
                ].map((item) => (
                  <li key={item.text} className="flex gap-3">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-[#00E5A0]" />
                    <div>
                      <p className="text-sm font-medium text-white">{item.text}</p>
                      <p className="mt-0.5 text-xs text-white/40">{item.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise scenarios */}
            <div className="rounded-2xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.03] p-8 sm:p-10">
              <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-[#00E5A0]/20">
                <ShieldIcon className="size-6 text-[#00E5A0]" />
              </div>
              <h3 className="text-lg font-bold text-white">Choose Enterprise if…</h3>
              <ul className="mt-5 space-y-4">
                {[
                  { text: 'You have 100+ employees across teams', detail: 'Volume discounts, custom contracts, dedicated onboarding' },
                  { text: 'SSO is a requirement from IT or security', detail: 'SAML 2.0 / OIDC with Okta, Azure AD, or any IdP' },
                  { text: 'You operate in multiple countries', detail: 'Multi-country tax filing, cross-border payment orchestration' },
                  { text: 'You need compliance visibility', detail: 'SOC 2 reports, audit trails, JIT provisioning, DPA' },
                  { text: 'Your team wants priority support', detail: 'SLA-backed response times, dedicated account manager' },
                ].map((item) => (
                  <li key={item.text} className="flex gap-3">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-[#00E5A0]" />
                    <div>
                      <p className="text-sm font-medium text-white">{item.text}</p>
                      <p className="mt-0.5 text-xs text-white/40">{item.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── UPGRADE TRIGGERS ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Upgrade path
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              When to upgrade from Growth
            </h2>
            <p className="mt-4 text-base text-white/50 leading-relaxed">
              Growth scales with you. Upgrade to Enterprise when you hit these milestones.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-4xl">
            {/* Trigger cards row */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: LockIcon,
                  trigger: 'IT requires SSO',
                  detail: 'Your security team mandates SAML or OIDC for all SaaS tools. Enterprise includes SSO with auto-provisioning.',
                },
                {
                  icon: GlobeIcon,
                  trigger: 'Expanding to a new country',
                  detail: 'You opened an office in Mexico or Nigeria. You need multi-country tax filing and local payment formats.',
                },
                {
                  icon: UsersIcon,
                  trigger: 'Crossed 100 employees',
                  detail: 'Volume discounts kick in. Enterprise unlocks custom contracts and dedicated onboarding for large teams.',
                },
                {
                  icon: BadgeCheckIcon,
                  trigger: 'SOC 2 audit approaching',
                  detail: 'Your auditor or prospect asks for compliance reports. Enterprise includes SOC 2 Type II evidence.',
                },
                {
                  icon: HeadphonesIcon,
                  trigger: 'Need SLA-backed support',
                  detail: 'Your team can no longer wait for best-effort response times. Enterprise includes guaranteed SLAs.',
                },
              ].map((item) => (
                <div
                  key={item.trigger}
                  className="group relative flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-colors hover:border-[#00E5A0]/20 hover:bg-white/[0.04]"
                >
                  <div className="flex size-10 items-center justify-center rounded-lg bg-[#00E5A0]/10 text-[#00E5A0] transition-colors group-hover:bg-[#00E5A0]/20">
                    <item.icon className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.trigger}</p>
                    <p className="mt-2 text-xs leading-relaxed text-white/40">{item.detail}</p>
                  </div>
                  <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#00E5A0] opacity-0 transition-opacity group-hover:opacity-100">
                    Upgrade to Enterprise <ChevronRightIcon className="size-3" />
                  </span>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 flex items-center justify-center gap-4 rounded-2xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.04] p-6 sm:p-8">
              <div className="hidden sm:flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#00E5A0]/20">
                <RocketIcon className="size-6 text-[#00E5A0]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">
                  Already on Growth and hitting these triggers?
                </p>
                <p className="mt-1 text-xs text-white/40">
                  Upgrade from your billing dashboard in one click — pricing
                  adjusts automatically. No migration, no downtime.
                </p>
              </div>
              <Link
                to="/dashboard/billing"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#00E5A0] px-5 py-2.5 text-sm font-semibold text-[#0B1120] hover:bg-[#00E5A0]/90 transition-colors"
              >
                Upgrade now <ArrowUpRightIcon className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURE COMPARISON TABLE ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Compare plans
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Feature comparison
            </h2>
          </div>

          <div className="mx-auto mt-12 max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03]">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-white/40">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-white/40">
                      Growth
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-[#00E5A0]">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((row, i) => (
                    <tr
                      key={row.feature}
                      className={`border-b border-white/5 ${i % 2 === 0 ? '' : 'bg-white/[0.02]'}`}
                    >
                      <td className="flex items-center gap-3 px-6 py-4">
                        <row.icon className="size-4 shrink-0 text-white/20" />
                        <span className="text-sm text-white/70">{row.feature}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <CellValue value={row.growth} />
                      </td>
                      <td className="px-6 py-4 text-center">
                        <CellValue value={row.enterprise} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ADD-ONS ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Extend your plan
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Add-ons
            </h2>
            <p className="mt-4 text-base text-white/50 leading-relaxed">
              Pay only for what you need. Add connections, countries, or capacity as you grow.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-3xl gap-4 sm:grid-cols-2">
            {addons.map((addon) => (
              <div
                key={addon.name}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <p className="text-sm font-medium text-white">{addon.name}</p>
                <p className="mt-2 text-lg font-semibold text-[#00E5A0]">{addon.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROI CALCULATOR ─── */}
      <section className={`${sectionPy} border-t border-white/5`}>
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#00E5A0]/30 bg-[#00E5A0]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#00E5A0]">
              <TrendingDownIcon className="size-3.5" />
              Savings calculator
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              How much can you save?
            </h2>
            <p className="mt-4 text-base text-white/50 leading-relaxed">
              Compare manual bank file processing against PeerPass automation.
              Adjust the sliders to match your team.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-5xl">
            <div className="grid gap-8 lg:grid-cols-2">
              {/* ── Inputs ── */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-white/30">Your current costs</p>

                {/* Employees slider */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                      <UsersIcon className="size-4 text-white/40" />
                      Employees
                    </label>
                    <span className="text-sm font-semibold text-[#00E5A0]">{roiEmployees}</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={500}
                    step={1}
                    value={roiEmployees}
                    onChange={(e) => setRoiEmployees(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-white/10 accent-[#00E5A0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00E5A0] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#00E5A0]/30"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-white/25">
                    <span>1</span>
                    <span>500+</span>
                  </div>
                </div>

                {/* Cycles per month slider */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                      <RefreshCwIcon className="size-4 text-white/40" />
                      Payment cycles/month
                    </label>
                    <span className="text-sm font-semibold text-[#00E5A0]">{roiCyclesPerMonth}×</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={8}
                    step={1}
                    value={roiCyclesPerMonth}
                    onChange={(e) => setRoiCyclesPerMonth(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-white/10 accent-[#00E5A0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00E5A0] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#00E5A0]/30"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-white/25">
                    <span>1 (monthly)</span>
                    <span>8 (weekly)</span>
                  </div>
                </div>

                {/* Hourly rate slider */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                      <BanknoteIcon className="size-4 text-white/40" />
                      Staff hourly rate
                    </label>
                    <span className="text-sm font-semibold text-[#00E5A0]">€{roiHourlyRate}/hr</span>
                  </div>
                  <input
                    type="range"
                    min={15}
                    max={100}
                    step={5}
                    value={roiHourlyRate}
                    onChange={(e) => setRoiHourlyRate(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-white/10 accent-[#00E5A0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00E5A0] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#00E5A0]/30"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-white/25">
                    <span>€15/hr</span>
                    <span>€100/hr</span>
                  </div>
                </div>

                {/* Minutes per file slider */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                      <ClockIcon className="size-4 text-white/40" />
                      Minutes per payment file
                    </label>
                    <span className="text-sm font-semibold text-[#00E5A0]">{roiMinutesPerFile} min</span>
                  </div>
                  <input
                    type="range"
                    min={5}
                    max={120}
                    step={5}
                    value={roiMinutesPerFile}
                    onChange={(e) => setRoiMinutesPerFile(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-white/10 accent-[#00E5A0] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00E5A0] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#00E5A0]/30"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-white/25">
                    <span>5 min</span>
                    <span>2 hrs</span>
                  </div>
                </div>

                {/* Countries */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                      <GlobeIcon className="size-4 text-white/40" />
                      Countries
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setRoiCountries((c) => Math.max(1, c - 1))}
                        className="flex size-7 items-center justify-center rounded-lg border border-white/10 hover:border-white/20 text-white/60 hover:text-white transition-colors"
                        aria-label="Decrease countries"
                      >
                        <MinusIcon className="size-3.5" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold text-[#00E5A0]">{roiCountries}</span>
                      <button
                        onClick={() => setRoiCountries((c) => Math.min(10, c + 1))}
                        className="flex size-7 items-center justify-center rounded-lg border border-white/10 hover:border-white/20 text-white/60 hover:text-white transition-colors"
                        aria-label="Increase countries"
                      >
                        <PlusIcon className="size-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Results ── */}
              <div className="flex flex-col gap-5">
                {/* Manual cost card */}
                <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-6 sm:p-8">
                  <p className="text-xs font-semibold uppercase tracking-widest text-red-400/60">
                    Current manual cost
                  </p>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-red-300">
                      €{Math.round(roiManualMonthlyCost).toLocaleString()}
                    </span>
                    <span className="text-sm text-red-400/50">/month</span>
                  </div>
                  <p className="mt-2 text-xs text-red-400/40">
                    {roiCyclesPerMonth} cycle{roiCyclesPerMonth > 1 ? 's' : ''}/month × {roiCountries} countr{roiCountries > 1 ? 'ies' : 'y'} × {roiMinutesPerFile} min @ €{roiHourlyRate}/hr
                  </p>
                </div>

                {/* PeerPass cost card */}
                <div className="rounded-2xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.04] p-6 sm:p-8">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#00E5A0]/60">
                    PeerPass automated cost
                  </p>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-[#00E5A0]">
                      €{Math.round(roiPlatformMonthly).toLocaleString()}
                    </span>
                    <span className="text-sm text-[#00E5A0]/50">/month</span>
                  </div>
                  <p className="mt-2 text-xs text-[#00E5A0]/40">
                    {roiEmployees} employees × {currencySymbol}{formatCents(growthCents)}/user on Growth plan — setup once, runs automatically
                  </p>
                </div>

                {/* Savings stat */}
                <div className="rounded-2xl border border-[#00E5A0]/20 bg-gradient-to-br from-[#00E5A0]/10 to-[#00E5A0]/[0.02] p-6 sm:p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-white/30">
                        Monthly savings
                      </p>
                      <p className="mt-2 text-3xl font-bold text-white">
                        €{Math.round(Math.max(0, roiMonthlySavings)).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-white/30">
                        Annual savings
                      </p>
                      <p className="mt-2 text-3xl font-bold text-[#00E5A0]">
                        €{Math.round(Math.max(0, roiAnnualSavings)).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-white/5 pt-5">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-xl bg-[#00E5A0]/15">
                        <ClockIcon className="size-5 text-[#00E5A0]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">
                          {Math.round(roiHoursSavedPerYear)} hours saved per year
                        </p>
                        <p className="text-xs text-white/40">
                          That's {Math.round(roiHoursSavedPerYear / 8)} working days your team gets back
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <a href="/signup">
                  <Button
                    size="lg"
                    className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/20"
                  >
                    Start saving with a free trial
                    <ArrowRightIcon className="ml-2 size-4" />
                  </Button>
                </a>
              </div>
            </div>

            <p className="mt-8 text-center text-xs text-white/25">
              Estimates based on manual NACHA/SEPA/BACS file preparation time. PeerPass cost
              uses Growth plan pricing ({currencySymbol}{formatCents(growthMonthlyCents)}/user/month).
              Actual savings depend on your region, employee count, and payment complexity.
              Enterprise pricing also available.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className={`${sectionPy} border-t border-white/5`} id="faq">
        <div className={containerW}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              Questions?
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Frequently asked questions
            </h2>
          </div>

          <div className="mx-auto mt-12 max-w-3xl space-y-6">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] transition-colors open:bg-white/[0.04]"
              >
                <summary className="flex cursor-pointer items-center justify-between px-6 py-5 text-sm font-medium text-white list-none">
                  {faq.q}
                  <span className="ml-4 shrink-0 text-white/30 transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="px-6 pb-5 text-sm leading-relaxed text-white/50">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BOTTOM CTA ─── */}
      <section className="relative overflow-hidden border-t border-white/5 py-20 sm:py-28">
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 size-[500px] rounded-full bg-[#00E5A0]/8 blur-[120px]" />
        <div className={`${containerW} relative z-10 text-center`}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            Ready to get started?
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-white/50 leading-relaxed">
            Start your 90-day free trial today. No credit card required.
            Full access to every Growth feature from day one.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="/signup">
              <Button
                size="lg"
                className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base px-8 py-6 shadow-xl shadow-[#00E5A0]/25"
              >
                Start free trial
                <ArrowRightIcon className="ml-2 size-4" />
              </Button>
            </a>
            <a href="#book-demo">
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white/10 hover:text-white text-base px-8 py-6"
              >
                Book a demo
              </Button>
            </a>
          </div>
          <p className="mt-6 text-xs text-white/30">
            Enterprise customers:{' '}
            <a href="#book-demo" className="text-[#00E5A0] hover:underline">
              contact sales
            </a>{' '}
            for custom pricing and volume discounts.
          </p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-white/5 py-8">
        <div className={`${containerW} flex flex-col items-center justify-between gap-4 sm:flex-row`}>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-8 items-center justify-center rounded-lg bg-[#00E5A0] text-[#0B1120] text-xs font-bold">
              PP
            </div>
            <span className="text-sm font-semibold text-white">PeerPass</span>
          </Link>
          <div className="flex items-center gap-6 text-xs text-white/30">
            <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
            <Link to="/pricing" className="text-white/60">Pricing</Link>
            <Link to="/login" className="hover:text-white/60 transition-colors">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
